<template>
  <div class="m-auto relative w-full">
    <div class="text-center text-black">
      <p
        v-if="!snapshotFile"
        class="!m-0 mt-1"
      >
        {{ statusText }}
      </p>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: progress + '%' }"
        />
      </div>
    </div>
    <video
      ref="videoRef"
      autoplay
      class="block w-full rounded-xl"
      :class="isCameraOn ? '' : 'hidden'"
      playsinline
      :style="{ transform: 'scaleX(-1)' }"
    />
    <canvas
      ref="canvasRef"
      class="absolute top-0 left-0 w-full h-full rounded-lg"
    />
  </div>
</template>

<script>
import * as faceapi from 'face-api.js'

const CONSTANTS = {
  CAMERA_CONFIG: {
    facingMode: 'user',
    height: { ideal: 1280 },
    width: { ideal: 720 },
  },
  DETECTION_INTERVAL: 300,
  DETECTION_OPTIONS: {
    inputSize: 224,
    scoreThreshold: 0.7,
  },
  FRAME_BOUNDS: {
    HEIGHT_RATIO: 0.7,
    WIDTH_RATIO: 0.6,
    X_RATIO: 0.2,
    Y_RATIO: 0.15,
  },
  IMAGE_QUALITY: 0.95,
  MODEL_URL: 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/',
  THRESHOLDS: {
    FACE_CLOSE: 0.65,
    FACE_SIZE_MIN: 0.5,
    HEAD_TURN: 0.15,
    SMILE: 0.9,
    HEAD_STRAIGHT: 0.05,
  },
}

const STATUS_MESSAGES = {
  CAMERA_ERROR: 'Tidak dapat mengakses kamera.',
  MODEL_LOAD_ERROR: 'Gagal memuat model AI.',
  MOVE_CLOSER: 'Mohon dekatkan wajah ke kamera',
  POSITION_FACE: 'Posisikan wajah Anda di dalam bingkai.',
  SMILE: 'Silahkan senyum 😊',
  TURN_LEFT: 'Silahkan gelengkan kepala ke Kiri.',
  TURN_RIGHT: 'Silahkan gelengkan kepala ke kanan.',
  HEAD_STRAIGHT: 'Posisikan wajah lurus ke depan',
}

export default {
  data() {
    return {
      // Liveness store state
      snapshotFile: null,
      currentStep: 1,
      modelsLoaded: false,
      progress: 0,
      displayProgress: 0,
      isCameraOn: false,
      cameraLoadProgress: 0,
      isCameraLoading: false,
      hasTurnedLeft: false,
      hasTurnedRight: false,

      // Component state
      statusText: '',
      faceInFrameSince: null,
      detectionInterval: null,
      animationFrameId: null,
      stream: null,
    }
  },
  mounted() {
    this.loadModels()
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    isHeadStraight(landmarks, boxWidth) {
      const offset = this.calculateHeadTurnOffset(landmarks, boxWidth)
      return Math.abs(offset) <= CONSTANTS.THRESHOLDS.HEAD_STRAIGHT
    },
    /**
     * Converts a data URL to a File object
     */
    dataURLtoFile(dataUrl, filename) {
      const [header, base64Data] = dataUrl.split(',')
      const mimeMatch = header?.match(/:(.*?);/)

      if (!mimeMatch) {
        throw new Error('Invalid data URL format')
      }

      const mime = mimeMatch[1]
      const binaryString = atob(base64Data)
      const bytes = new Uint8Array(binaryString.length)

      for (let i = 0; i < binaryString.length; i += 1) {
        bytes[i] = binaryString.charCodeAt(i)
      }

      return new File([bytes], filename, { type: mime })
    },

    /**
     * Captures a snapshot from video element and returns as File
     */
    captureVideoSnapshot(video) {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Failed to get canvas context')
      }

      // Mirror the image
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      const dataUrl = canvas.toDataURL('image/jpeg', CONSTANTS.IMAGE_QUALITY)
      const file = this.dataURLtoFile(dataUrl, `face-verification-${Date.now()}.jpg`)

      return file
    },

    /**
     * Draws the progress ring on the canvas
     */
    draw(canvas, video) {
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const displaySize = {
        height: video.clientHeight,
        width: video.clientWidth,
      }

      // Ensure canvas matches video size
      if (canvas.width !== displaySize.width || canvas.height !== displaySize.height) {
        faceapi.matchDimensions(canvas, displaySize)
      }

      this.clearCanvas(canvas)

      // Smoothly interpolate displayProgress towards actual progress
      // Lerp factor 0.1 for smooth animation
      this.displayProgress += (this.progress - this.displayProgress) * 0.1

      // Handle floating point errors near target
      if (Math.abs(this.displayProgress - this.progress) < 0.1) {
        this.displayProgress = this.progress
      }

      const bounds = this.getFrameBounds(canvas)
      const radiusX = bounds.width / 2
      const radiusY = bounds.height / 2
      const centerX = bounds.x + radiusX
      const centerY = bounds.y + radiusY

      ctx.lineWidth = 5
      ctx.lineCap = 'round'

      // 1. Draw Gray Background Ring (Dashed)
      ctx.strokeStyle = '#e5e7eb' // Gray
      ctx.setLineDash([5, 5]) // Small gaps
      ctx.beginPath()
      ctx.ellipse(
        centerX,
        centerY,
        radiusX,
        radiusY,
        0,
        0,
        2 * Math.PI,
      )
      ctx.stroke()

      // 2. Draw Orange Progress Ring (Dashed, Partial)
      if (this.displayProgress > 0) {
        ctx.strokeStyle = '#34A770' // Orange (colorPrimary)
        ctx.setLineDash([5, 5]) // Match dash pattern
        ctx.beginPath()

        // Start from top (-Math.PI / 2)
        // Draw arc based on progress percentage
        const startAngle = -Math.PI / 2
        const endAngle = startAngle + (2 * Math.PI * (this.displayProgress / 100))

        ctx.ellipse(
          centerX,
          centerY,
          radiusX,
          radiusY,
          0,
          startAngle,
          endAngle,
        )
        ctx.stroke()
      }

      ctx.setLineDash([])
    },

    startDrawingLoop() {
      const loop = () => {
        const video = this.$refs.videoRef
        const canvas = this.$refs.canvasRef

        if (video && canvas && !video.paused && !video.ended) {
          this.draw(canvas, video)
        }

        this.animationFrameId = requestAnimationFrame(loop)
      }
      this.animationFrameId = requestAnimationFrame(loop)
    },

    /**
     * Clears the entire canvas
     */
    clearCanvas(canvas) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    },

    /**
     * Calculates frame bounds based on canvas dimensions
     */
    getFrameBounds(canvas) {
      return {
        height: canvas.height * CONSTANTS.FRAME_BOUNDS.HEIGHT_RATIO,
        width: canvas.width * CONSTANTS.FRAME_BOUNDS.WIDTH_RATIO,
        x: canvas.width * CONSTANTS.FRAME_BOUNDS.X_RATIO,
        y: canvas.height * CONSTANTS.FRAME_BOUNDS.Y_RATIO,
      }
    },

    /**
     * Calculates head turn offset based on facial landmarks
     */
    calculateHeadTurnOffset(landmarks, boxWidth) {
      const nose = landmarks.getNose()[3]
      const leftEye = landmarks.getLeftEye()[0]
      const rightEye = landmarks.getRightEye()[3]
      const eyesCenterX = (leftEye.x + rightEye.x) / 2

      return (nose.x - eyesCenterX) / boxWidth
    },

    /**
     * Checks if face is properly positioned within frame bounds
     */
    isFaceInFrame(detection, frameBounds) {
      const { box } = detection
      const faceCenterX = box.x + box.width / 2
      const faceCenterY = box.y + box.height / 2

      const isInBounds = faceCenterX > frameBounds.x
        && faceCenterX < frameBounds.x + frameBounds.width
        && faceCenterY > frameBounds.y
        && faceCenterY < frameBounds.y + frameBounds.height

      const isLargeEnough = box.width > frameBounds.width * CONSTANTS.THRESHOLDS.FACE_SIZE_MIN
        && box.height > frameBounds.height * CONSTANTS.THRESHOLDS.FACE_SIZE_MIN

      return isInBounds && isLargeEnough
    },

    /**
     * Determines if person is smiling based on expressions
     */
    isSmiling(expressions) {
      return (expressions.happy || 0) > CONSTANTS.THRESHOLDS.SMILE
    },

    /**
     * Stops the camera stream
     */
    stopCamera() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
      }

      const video = this.$refs.videoRef
      if (!video || !video.srcObject) return

      const stream = video.srcObject
      stream.getTracks().forEach(track => {
        track.stop()
      })

      video.srcObject = null
    },

    /**
     * Process step one - turn right
     */
    processStepOne(resizedDetection, boxWidth) {
      const headTurnOffset = this.calculateHeadTurnOffset(resizedDetection.landmarks, boxWidth)

      if (headTurnOffset < -CONSTANTS.THRESHOLDS.HEAD_TURN) {
        this.hasTurnedRight = true
        this.progress = 33
        this.currentStep = 2
        this.statusText = STATUS_MESSAGES.TURN_LEFT
      } else {
        this.statusText = STATUS_MESSAGES.TURN_RIGHT
      }
    },

    /**
     * Process step two - turn left
     */
    processStepTwo(resizedDetection, boxWidth) {
      const headTurnOffset = this.calculateHeadTurnOffset(resizedDetection.landmarks, boxWidth)

      if (headTurnOffset > CONSTANTS.THRESHOLDS.HEAD_TURN) {
        this.hasTurnedLeft = true
        this.progress = 66
        this.currentStep = 3
        this.statusText = STATUS_MESSAGES.SMILE
      } else {
        this.statusText = STATUS_MESSAGES.TURN_LEFT
      }
    },

    /**
     * Process step three - smile and capture
     */
    processStepThree(resizedDetection, frameBounds) {
      const { box } = resizedDetection.detection
      const boxWidth = box.width

      const isCloseEnough = box.width > frameBounds.width * CONSTANTS.THRESHOLDS.FACE_CLOSE

      if (!isCloseEnough) {
        this.statusText = STATUS_MESSAGES.MOVE_CLOSER
        return false
      }

      if (!this.isHeadStraight(resizedDetection.landmarks, boxWidth)) {
        this.statusText = STATUS_MESSAGES.HEAD_STRAIGHT
        return false
      }

      if (!this.isSmiling(resizedDetection.expressions)) {
        this.statusText = STATUS_MESSAGES.SMILE
        return false
      }

      // ✅ all valid → cant capture
      if (!this.$refs.videoRef) return false

      const video = this.$refs.videoRef
      const file = this.captureVideoSnapshot(video)
      this.snapshotFile = file
      this.progress = 100
      this.$emit('snapshot-captured', file)
      setTimeout(() => {
        this.stopCamera()
        this.isCameraOn = false
      }, 1000)
      return true
    },

    /**
     * Process face detection
     */
    processDetection(detection, displaySize, frameBounds) {
      if (!detection) {
        this.faceInFrameSince = null
        return
      }

      const resizedDetection = faceapi.resizeResults(detection, displaySize)

      const faceInFrame = this.isFaceInFrame(resizedDetection.detection, frameBounds)

      if (!faceInFrame) {
        this.faceInFrameSince = null
        if (this.currentStep === 1) {
          this.statusText = STATUS_MESSAGES.POSITION_FACE
        }
        return
      }

      // Face is in frame, process based on current step
      const boxWidth = resizedDetection.detection.box.width

      switch (this.currentStep) {
        case 1:
          this.processStepOne(resizedDetection, boxWidth)
          break
        case 2:
          this.processStepTwo(resizedDetection, boxWidth)
          break
        case 3:
          this.processStepThree(resizedDetection, frameBounds)
          break
        default:
          break
      }
    },

    /**
     * Load face-api models
     */
    async loadModels() {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(CONSTANTS.MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(CONSTANTS.MODEL_URL),
          faceapi.nets.faceExpressionNet.loadFromUri(CONSTANTS.MODEL_URL),
        ])
        this.modelsLoaded = true
      } catch (error) {
        console.error('Failed to load models:', error)
        this.statusText = STATUS_MESSAGES.MODEL_LOAD_ERROR
      }
    },

    /**
     * Start camera
     */
    async startCamera() {
      if (!this.modelsLoaded) return

      this.isCameraLoading = true
      this.cameraLoadProgress = 0

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: CONSTANTS.CAMERA_CONFIG,
        })

        const video = this.$refs.videoRef
        if (video) {
          video.srcObject = stream
          this.cameraLoadProgress = 50

          video.onloadedmetadata = () => {
            this.cameraLoadProgress = 100
            this.isCameraLoading = false
            this.startDetection()
            this.startDrawingLoop()
          }
        }
      } catch (error) {
        console.error('Failed to start camera:', error)
        this.statusText = STATUS_MESSAGES.CAMERA_ERROR
        this.isCameraLoading = false
        this.cameraLoadProgress = 0
      }
    },

    /**
     * Start face detection loop
     */
    startDetection() {
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
      }

      const detectionOptions = new faceapi.TinyFaceDetectorOptions(CONSTANTS.DETECTION_OPTIONS)

      this.detectionInterval = setInterval(async () => {
        if (this.progress === 100) {
          clearInterval(this.detectionInterval)
          return
        }

        const video = this.$refs.videoRef
        // Canvas drawing is now handled in startDrawingLoop

        if (!video || video.readyState < 3) return

        try {
          const detection = await faceapi
            .detectSingleFace(video, detectionOptions)
            .withFaceLandmarks(true)
            .withFaceExpressions()

          const displaySize = {
            height: video.clientHeight,
            width: video.clientWidth,
          }

          // We still need bounds for logic checking
          // Create a temporary canvas or calculate bounds directly if possible
          // But getFrameBounds relies on canvas dimensions.
          // We can use the canvas ref purely for dimension reading here or pass displaySize
          // Actually getFrameBounds uses canvas.width/height which match video/displaySize

          const frameBounds = {
            height: displaySize.height * CONSTANTS.FRAME_BOUNDS.HEIGHT_RATIO,
            width: displaySize.width * CONSTANTS.FRAME_BOUNDS.WIDTH_RATIO,
            x: displaySize.width * CONSTANTS.FRAME_BOUNDS.X_RATIO,
            y: displaySize.height * CONSTANTS.FRAME_BOUNDS.Y_RATIO,
          }

          this.processDetection(detection, displaySize, frameBounds)
        } catch (error) {
          console.error('Detection error:', error)
        }
      }, CONSTANTS.DETECTION_INTERVAL)
    },

    /**
     * Reset verification process
     */
    resetVerification() {
      this.progress = 0
      this.currentStep = 1
      this.hasTurnedLeft = false
      this.hasTurnedRight = false
      this.snapshotFile = null
      this.faceInFrameSince = null
      this.statusText = STATUS_MESSAGES.TURN_RIGHT

      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
        this.detectionInterval = null
      }
    },

    /**
     * Enable camera
     */
    enableCamera() {
      this.isCameraOn = true
      this.startCamera()
    },

    /**
     * Disable camera
     */
    disableCamera() {
      this.isCameraOn = false
      this.stopCamera()
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
        this.detectionInterval = null
      }
    },

    /**
     * Cleanup on component destroy
     */
    cleanup() {
      this.stopCamera()
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
      }
    },
  },
}
</script>
